/**
 * Pure validator for the encrypted-local-backup passphrase (web).
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/BackupPassphrasePolicy.swift` in
 * intent (strength gate before sealing) but uses the web spec's simpler
 * rule-set: 6..128 chars, must contain at least one non-whitespace char.
 * The web baseline accepts shorter passphrases than iOS because PBKDF2
 * with 600 000 iterations on `crypto.subtle` is roughly 40x as expensive
 * per guess as iOS's 16k iterated-HKDF; the offline-bruteforce cost
 * budget rebalances toward the KDF and away from minimum-length.
 *
 * THREAT MODEL
 *   - Weak-passphrase brute force: a sub-6-char passphrase against a
 *     600k-iteration PBKDF2 backup is still feasible to enumerate.
 *     Defense: hard minimum of 6 chars.
 *   - All-whitespace input: trivially guessed (one input space). Defense:
 *     reject empty + reject all-whitespace.
 *   - Memory exhaustion via pathological input: a multi-MB passphrase
 *     would round-trip through `crypto.subtle.importKey` and consume
 *     proportional memory. Defense: hard maximum of 128 chars.
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs. Pure logic.
 */

/** Hard floor for accepted passphrases. Below this is rejected. */
export const MIN_PASSPHRASE_LENGTH = 6;

/** Hard ceiling for accepted passphrases. Above this is rejected. */
export const MAX_PASSPHRASE_LENGTH = 128;

/**
 * Outcome of `validatePassphrase`. The discriminated `reason` lets the UI
 * render targeted copy ("too short" vs "all whitespace") rather than a
 * single opaque "invalid passphrase" string.
 */
export type PassphraseValidation =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly reason: "empty" | "tooShort" | "tooLong" | "allWhitespace";
    };

/**
 * Validate a passphrase against the backup policy. Pure, idempotent, no I/O.
 * Returns a discriminated result rather than throwing — the caller decides
 * whether to throw, log, or render UI.
 *
 * Mirrors iOS `BackupPassphrasePolicy.validate(passphrase:)` shape but the
 * rule-set is the web spec's (see file header for the rationale).
 */
export function validatePassphrase(passphrase: string): PassphraseValidation {
  if (passphrase.length === 0) {
    return { ok: false, reason: "empty" };
  }
  if (passphrase.length < MIN_PASSPHRASE_LENGTH) {
    return { ok: false, reason: "tooShort" };
  }
  if (passphrase.length > MAX_PASSPHRASE_LENGTH) {
    return { ok: false, reason: "tooLong" };
  }
  // `\s` in JS matches the Unicode whitespace set (space, tab, NBSP, line
  // terminators, etc.). An all-whitespace passphrase like "      " has
  // ~zero entropy regardless of length, so it is rejected unconditionally.
  if (/^\s+$/.test(passphrase)) {
    return { ok: false, reason: "allWhitespace" };
  }
  return { ok: true };
}
